import React from 'react'
import { Box } from '@mui/material'
import { ZodTypeAny } from 'zod'
import { FieldError } from '../../fieldPrimitives'
import { Section } from '../historyLayoutKit'

const pathsOf = (path: string | string[]) => (Array.isArray(path) ? path : [path])

// The closest field is the one at the issue's path, or the nearest ancestor; a group falls back to its section.
const labelFor = (path: string, sections: Section[]): string => {
  const fields = sections.flatMap((section) => section.fields.map((field) => ({ field, section })))
  const covering = fields
    .flatMap(({ field, section }) => pathsOf(field.path).map((own) => ({ own, field, section })))
    .filter(({ own }) => own === path || path.startsWith(`${own}.`))
    .sort((a, b) => b.own.length - a.own.length)[0]

  if (covering) {
    return covering.field.label || covering.section.title || path
  }

  const inside = fields.find(({ field }) => pathsOf(field.path).some((own) => own.startsWith(`${path}.`)))

  return inside?.section.title ?? path
}

export type Problem = { path: string; label: string; message: string }

export const validationProblems = (schema: ZodTypeAny, value: unknown, sections: Section[] = []): Problem[] => {
  const parsed = schema.safeParse(value)

  return parsed.success
    ? []
    : parsed.error.issues.map((issue) => {
        const path = issue.path.join('.')

        return { path, label: labelFor(path, sections), message: issue.message }
      })
}

export const validationIssues = (schema: ZodTypeAny, value: unknown, sections: Section[] = []): string[] => [
  ...new Set(validationProblems(schema, value, sections).map(({ label, message }) => `${label}: ${message}`)),
]

export const ValidationIssues = ({ issues }: { issues: string[] }) =>
  issues.length === 0 ? null : (
    <Box role="alert" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {issues.map((issue) => (
        <FieldError key={issue} message={issue} />
      ))}
    </Box>
  )
